import { useState } from "react";
import { SITE_TAGLINE } from "../lib/siteBranding";
import { SiteInfoModal } from "./SiteInfoModal";
import { SiteNameWordmark } from "./SiteNameWordmark";

type HeaderProps = {
  searchQuery: string;
  onSearchChange: (q: string) => void;
};

export function Header({ searchQuery, onSearchChange }: HeaderProps) {
  const [infoOpen, setInfoOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1920px] items-center gap-2 py-0 pl-2 pr-3 sm:gap-3 sm:pl-3 sm:pr-4">
        <a
          href="/"
          className="flex shrink-0 items-center gap-2 text-primary transition hover:opacity-90 sm:gap-2.5"
        >
          <picture>
            <source srcSet="/job_301_logo-header.webp" type="image/webp" />
            <img
              src="/job_301_logo-header.png"
              alt=""
              width={192}
              height={192}
              decoding="async"
              fetchPriority="high"
              className="h-[72px] w-auto shrink-0 rounded-lg object-contain object-left"
              title={SITE_TAGLINE}
            />
          </picture>
          <span className="flex min-w-0 flex-col leading-tight">
            <SiteNameWordmark className="text-base sm:text-lg" />
            <span className="text-[10px] font-normal leading-snug text-slate-500 sm:text-[11px]">
              {SITE_TAGLINE}
            </span>
          </span>
        </a>

        <div className="mx-auto hidden min-w-0 max-w-xl flex-1 md:block">
          <label htmlFor="job-search" className="sr-only">
            Search jobs
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M16 16l5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <input
              id="job-search"
              type="search"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search title or company…"
              className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50/80 py-2 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 transition focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        <div className="ml-auto flex shrink-0 items-center">
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-label="About this site"
            onClick={() => setInfoOpen(true)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M12 10v6M12 8h.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <SiteInfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />

      <div className="border-t border-slate-100 px-2 py-2 sm:px-3 md:hidden">
        <label htmlFor="job-search-mobile" className="sr-only">
          Search jobs
        </label>
        <input
          id="job-search-mobile"
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search title or company…"
          className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>
    </header>
  );
}
